import { useCallback, useEffect, useMemo, useRef, useState, ReactElement } from "react";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { useSearchFirehose } from "../../firehose-all/hooks/useSearchFirehose";
import { Portfolio } from "@/types/Portfolio";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { NewsFeedItem } from "@/shared/api/firehose/models";
import { BackendSource, SearchStoriesParams } from "@/shared/api/firehose";
import { applyFilters } from "./useFirehoseHeaderFilterMenu";
import { useFirehoseFilterPanel } from "./useFirehoseFilterPanel";
import { useFirehoseHighlightPanel } from "./useFirehoseHighlightPanel";
import { useSettingsStore } from "@/stores/SettingsStore";
import useStableArray from "@/hooks/useStableArray";
import { buildSearchTitle, mapPortfolioIdsToPortfolios } from "./searchUtils";
import { useFirehoseSources } from "./useFirehoseSources";
import { FilterMenuConfig } from "@/shared/models/filterTypes";
import { useFirehoseFilterStore } from "../FilterStore";
import { trackFilterApplied } from "@/utils/gtm";

interface UseFirehoseAdvancedSearchPanelOptions {
  /** The story type key in the filter store (e.g., 'allStories', 'stocks-news') */
  storyType: ("allStories" | BackendSource)[];
  /** Original data to return when no filters are active */
  originalData: NewsFeedItem[];
  /** Optional callback when search is performed */
  onSearchPerformed?: (searchQuery: {
    keywords: string[];
    tickers: string[];
  }) => void;
  /** Optional callback to set the widget title */
  setTitle?: (title: string) => void;
  /** Default title when no search is active */
  defaultTitle?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setActions?: (actions: ReactElement<any>[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pushAction?: (action: ReactElement<any>) => void;
  persistKey?: string; // Key to persist filter selections
  closeSearch?: () => void;
  onSearchOpen?: (callback: () => void) => () => void;
}

interface UseFirehoseAdvancedSearchPanelResult {
  /** The filtered data (either search results or original data) */
  filteredData: [NewsFeedItem, Portfolio | undefined][];
  /** Whether search results are currently being shown */
  showSearchResults: boolean;
  /** Loading state from the search */
  isSearching: boolean;
  /** Error state from the search */
  searchError: Error | null;
  /** The search function (exposed in case manual trigger is needed) */
  searchFirehose: (params: SearchStoriesParams) => void;
  /** Clear search results function */
  clearSearchResults: () => void;
  /** Currently selected advanced filter IDs */
  selectedAdvancedFilterIds: string[];
  /** Load next page of search results */
  loadNextPage: () => void;
  /** Whether more results are available */
  hasMore: boolean;
  /** Filter selections from the filter panel */
  filterSelections: Record<string, string[]>;
  /** Set filter selections */
  setFilterSelections: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  /** Whether the filter panel is open */
  isPanelOpen: boolean;
  /** Close the filter panel */
  closePanel: () => void;
  /** Filter menu configurations for the panel */
  filterMenus: FilterMenuConfig[];
  /** @deprecated Use clearFilterSelections instead */
  footerElement: React.ReactElement | null;
  /** Clear filter selections */
  clearFilterSelections: () => void;
  /** Highlight selections from the highlight panel */
  highlightSelections: Record<string, string[]>;
  /** Set highlight selections */
  setHighlightSelections: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  /** Whether the highlight panel is open */
  isHighlightPanelOpen: boolean;
  /** Close the highlight panel */
  closeHighlightPanel: () => void;
  /** Highlight menu configurations for the panel */
  highlightMenus: FilterMenuConfig[];
  /** @deprecated Use clearHighlightSelections instead */
  highlightFooterElement: React.ReactElement | null;
  /** Clear highlight selections */
  clearHighlightSelections: () => void;
}

/**
 * Hook that manages advanced filter search functionality for firehose widgets
 * using the new inline FilterPanel instead of the dropdown.
 */
export const useFirehoseAdvancedSearchPanel = ({
  storyType,
  originalData,
  onSearchPerformed,
  setTitle,
  defaultTitle,
  setActions,
  pushAction,
  persistKey,
  closeSearch,
  onSearchOpen,
}: UseFirehoseAdvancedSearchPanelOptions): UseFirehoseAdvancedSearchPanelResult => {
  const { portfolios } = usePortfoliosCRUD();
  const sourceColors = useSettingsStore((state) => state.sourceColors);
  const firehoseSources = useFirehoseSources();

  // Stabilize storyType array to prevent unnecessary re-renders
  const stableStoryType = useStableArray(storyType);

  const { queriesQuery } = useHeadlineQueriesCRUD();

  // Track which panel was last opened to coordinate closing
  const [lastOpenedPanel, setLastOpenedPanel] = useState<
    "filter" | "highlight" | null
  >(null);

  // Memoize onOpen callbacks to prevent infinite render loops
  // (inline arrow functions would create new references each render,
  // causing togglePanel to be recreated and pushAction effect to run infinitely)
  const handleHighlightPanelOpen = useCallback(() => {
    setLastOpenedPanel("highlight");
  }, []);

  const handleFilterPanelOpen = useCallback(() => {
    setLastOpenedPanel("filter");
  }, []);

  // Highlight panel
  const {
    selections: highlightSelections,
    setSelections: setHighlightSelections,
    isPanelOpen: isHighlightPanelOpen,
    closePanel: closeHighlightPanel,
    filterMenus: highlightMenus,
    footerElement: highlightFooterElement,
    clearSelections: clearHighlightSelections,
  } = useFirehoseHighlightPanel({
    sources: stableStoryType,
    portfolios,
    persistKey: persistKey,
    pushAction: pushAction,
    setActions: setActions,
    onOpen: handleHighlightPanelOpen,
    closeSearch,
    onSearchOpen,
  });

  // Filter panel
  const {
    selections: filterSelections,
    setSelections: setFilterSelections,
    isPanelOpen,
    closePanel,
    filterMenus,
    footerElement,
    filterMode,
    clearSelections: clearFilterSelections,
  } = useFirehoseFilterPanel({
    sources: stableStoryType,
    portfolios,
    persistKey: persistKey,
    pushAction: pushAction,
    setActions: setActions,
    onOpen: handleFilterPanelOpen,
    closeSearch,
    onSearchOpen,
  });

  // Panel coordination: when one opens, close the other
  // Note: Using refs to avoid effect running on every state change
  const closeHighlightPanelRef = useRef(closeHighlightPanel);
  const closePanelRef = useRef(closePanel);

  useEffect(() => {
    closeHighlightPanelRef.current = closeHighlightPanel;
    closePanelRef.current = closePanel;
  }, [closeHighlightPanel, closePanel]);

  useEffect(() => {
    if (lastOpenedPanel === "filter" && isHighlightPanelOpen) {
      closeHighlightPanelRef.current();
    } else if (lastOpenedPanel === "highlight" && isPanelOpen) {
      closePanelRef.current();
    }
  }, [lastOpenedPanel, isPanelOpen, isHighlightPanelOpen]);

  // Combined selections for applyFilters (needs both filter and highlight)
  const combinedSelections = useMemo(
    () => ({
      ...filterSelections,
      ...highlightSelections,
    }),
    [filterSelections, highlightSelections]
  );

  const {
    searchFirehose,
    searchResults,
    clearSearchResults,
    isLoading: isSearching,
    error: searchError,
    loadNextPage,
    hasMore,
  } = useSearchFirehose();

  // Use refs to maintain stable references
  const setTitleRef = useRef(setTitle);
  const onSearchPerformedRef = useRef(onSearchPerformed);
  const defaultTitleRef = useRef(defaultTitle);

  // Update refs when props change
  useEffect(() => {
    setTitleRef.current = setTitle;
    onSearchPerformedRef.current = onSearchPerformed;
    defaultTitleRef.current = defaultTitle;
  }, [setTitle, onSearchPerformed, defaultTitle]);

  // Get selected advanced filter IDs from the store
  // Memoize to prevent creating new array reference on every render
  const selectedAdvancedFilterIds = useMemo(
    () => filterSelections["category-filter"] ?? [],
    [filterSelections]
  );

  // Get selected portfolio filter IDs from the store (both ticker and keyword watchlists)
  // IMPORTANT: Memoized to prevent new array reference on every render,
  // which would cause the search effect to run on every render
  const selectedPortfolioFilterIds = useMemo(
    () => [
      ...(filterSelections["ticker-watchlists-filter"] || []),
      ...(filterSelections["keyword-watchlists-filter"] || []),
    ],
    [filterSelections]
  );

  // Get selected source filter IDs from the store
  // Memoize to prevent creating new array reference on every render
  const selectedSourceFilterIds = useMemo(
    () => filterSelections["sources-filter"] ?? [],
    [filterSelections]
  );

  const showSearchResults =
    (selectedAdvancedFilterIds && selectedAdvancedFilterIds.length > 0) ||
    (selectedPortfolioFilterIds && selectedPortfolioFilterIds.length > 0) ||
    (selectedSourceFilterIds && selectedSourceFilterIds.length > 0);

  // Determine which data to return
  const filteredData = useMemo(() => {
    const data = showSearchResults ? searchResults : originalData;

    // Apply portfolio highlight filters (using combinedSelections for both filter and highlight)
    let filteredData = applyFilters(data ?? [], combinedSelections, portfolios);

    // Apply source highlight filters
    const selectedSources = highlightSelections["sources-highlight"] ?? [];

    if (selectedSources.length > 0) {
      filteredData = filteredData.map((item) => {
        const [story, portfolio] = item;
        if (selectedSources.includes(story.backend_source)) {
          return [
            story,
            { ...portfolio, color: sourceColors[story.backend_source] },
          ] as [NewsFeedItem, Portfolio | undefined];
        }
        return item;
      });
    }

    return filteredData;
  }, [
    showSearchResults,
    searchResults,
    originalData,
    combinedSelections,
    highlightSelections,
    portfolios,
    sourceColors,
  ]);

  // Separate effect: Update title when any filter changes (including highlight filters)
  useEffect(() => {
    const hasSearchFilters = showSearchResults;

    if (hasSearchFilters) {
      // Get selected category queries
      const selectedAdvancedFilters =
        queriesQuery.data?.filter((q) =>
          selectedAdvancedFilterIds?.includes(String(q.id))
        ) ?? [];

      const selectedFilterPortfolios = mapPortfolioIdsToPortfolios(
        portfolios,
        selectedPortfolioFilterIds
      );

      // Get selected sources from filter menu
      const selectedSources = filterSelections["sources-filter"];

      // Map selected source IDs to labels
      const selectedSourceLabels = selectedSources
        ? firehoseSources
            .filter((source) => selectedSources.includes(source.source))
            .map((source) => source.label)
        : undefined;

      // Build search title
      const searchTitle = buildSearchTitle({
        selectedAdvancedFilters,
        selectedFilterPortfolio: selectedFilterPortfolios,
        selectedSourceLabels,
        defaultTitle: defaultTitleRef.current,
      });

      // Update title if callback provided (using ref for stable reference)
      if (setTitleRef.current) {
        setTitleRef.current(searchTitle);
      }
    } else {
      // Reset title if callback provided (using ref for stable reference)
      if (setTitleRef.current && defaultTitleRef.current) {
        setTitleRef.current(defaultTitleRef.current);
      }
    }

    // Cleanup function to reset title on unmount
    return () => {
      if (setTitleRef.current && defaultTitleRef.current) {
        setTitleRef.current(defaultTitleRef.current);
      }
    };
  }, [
    filterSelections,
    portfolios,
    queriesQuery.data,
    firehoseSources,
    selectedAdvancedFilterIds,
    selectedPortfolioFilterIds,
    selectedSourceFilterIds,
    showSearchResults,
  ]);

  // Use refs for stable callback references to avoid effect re-runs
  const searchFirehoseRef = useRef(searchFirehose);
  const clearSearchResultsRef = useRef(clearSearchResults);

  useEffect(() => {
    searchFirehoseRef.current = searchFirehose;
    clearSearchResultsRef.current = clearSearchResults;
  }, [searchFirehose, clearSearchResults]);

  // Track if search has been cleared to avoid redundant clears
  const hasSearchBeenClearedRef = useRef(false);

  // Separate effect: Perform search when search filters change (NOT highlight filters)
  useEffect(() => {
    if (showSearchResults) {
      // Search is active - reset the cleared flag
      hasSearchBeenClearedRef.current = false;

      // Get selected category queries
      const selectedAdvancedFilters =
        queriesQuery.data?.filter((q) =>
          selectedAdvancedFilterIds?.includes(String(q.id))
        ) ?? [];

      const allSelectedPortfolios = mapPortfolioIdsToPortfolios(
        portfolios,
        selectedPortfolioFilterIds
      );

      // Get selected sources from filter menu
      const selectedSources = filterSelections["sources-filter"];

      // Determine source parameter for backend
      let sourceParam: BackendSource[] | undefined;

      if (selectedSources && selectedSources.length > 0) {
        // User has selected specific sources, use those
        sourceParam = selectedSources as BackendSource[];
      } else if (!stableStoryType.includes("allStories")) {
        // Widget is for specific sources (not "all"), use widget type
        sourceParam = stableStoryType as BackendSource[];
      } else if (stableStoryType.includes("allStories")) {
        // For "allStories", pass accessible sources so backend doesn't return restricted ones
        sourceParam = firehoseSources.map((s) => s.source) as BackendSource[];
      }

      // Trigger the search
      searchFirehoseRef.current({
        advanceFilter: selectedAdvancedFilters[0],
        portfolios: allSelectedPortfolios,
        source: sourceParam,
      });
    } else if (!hasSearchBeenClearedRef.current) {
      // Clear search only once when no filters are selected
      // This prevents redundant clears when other deps change
      clearSearchResultsRef.current();
      hasSearchBeenClearedRef.current = true;
    }
  }, [
    portfolios,
    queriesQuery.data,
    selectedAdvancedFilterIds,
    selectedPortfolioFilterIds,
    selectedSourceFilterIds,
    stableStoryType,
    filterMode,
    showSearchResults,
    filterSelections,
  ]);

  // ── Track filter usage (debounced) ──────────────────────────────────
  const filterTrackingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!showSearchResults) return;
    if (filterTrackingTimer.current) clearTimeout(filterTrackingTimer.current);
    filterTrackingTimer.current = setTimeout(() => {
      if (selectedSourceFilterIds.length > 0) {
        trackFilterApplied({
          filter_context: "firehose",
          filter_type: "source",
          filter_value: selectedSourceFilterIds.join(", "),
          filter_count: selectedSourceFilterIds.length,
        });
      }
      if (selectedPortfolioFilterIds.length > 0) {
        const portfolioNames = selectedPortfolioFilterIds
          .map((id) => portfolios?.find((p) => String(p.id) === String(id))?.name)
          .filter(Boolean)
          .join(", ");
        trackFilterApplied({
          filter_context: "firehose",
          filter_type: "portfolio",
          filter_value: portfolioNames || selectedPortfolioFilterIds.join(", "),
          filter_count: selectedPortfolioFilterIds.length,
        });
      }
      if (selectedAdvancedFilterIds.length > 0) {
        const filterNames = queriesQuery.data
          ?.filter((q) => selectedAdvancedFilterIds.includes(String(q.id)))
          .map((q) => q.queryName)
          .join(", ");
        trackFilterApplied({
          filter_context: "firehose",
          filter_type: "advanced_filter",
          filter_value: filterNames || selectedAdvancedFilterIds.join(", "),
          filter_count: selectedAdvancedFilterIds.length,
        });
      }
    }, 500);
    return () => {
      if (filterTrackingTimer.current) clearTimeout(filterTrackingTimer.current);
    };
  }, [selectedSourceFilterIds, selectedPortfolioFilterIds, selectedAdvancedFilterIds, portfolios, queriesQuery.data, showSearchResults]);

  // Get global filter store actions for syncing portfolio selections (for sound alerts)
  const setPortfoliosToFilter = useFirehoseFilterStore(
    (state) => state.setPortfoliosToFilter
  );
  const setPortfoliosToHighlight = useFirehoseFilterStore(
    (state) => state.setPortfoliosToHighlight
  );

  // Sync portfolio filter/highlight selections to global store for sound alerts.
  // Includes portfolios embedded in active advanced filters (CategoryQueries)
  // so a portfolio with sound enabled fires even when it's only referenced via
  // a CategoryQuery rather than a standalone watchlist selection.
  useEffect(() => {
    const advancedFilterPortfolioIds =
      queriesQuery.data
        ?.filter((q) => selectedAdvancedFilterIds?.includes(String(q.id)))
        .flatMap((q) => (q.portfolios || []).map((p) => p.id)) ?? [];

    const highlightPortfolioIds = [
      ...new Set(
        [
          ...(highlightSelections["ticker-watchlists-highlight"] ?? []),
          ...(highlightSelections["keyword-watchlists-highlight"] ?? []),
        ].map((id) => Number(id))
      ),
    ];
    const filterPortfolioIds = [
      ...new Set([
        ...[
          ...(filterSelections["portfolios-filter"] ?? []),
          ...(filterSelections["portfolios-filter-prebuilt"] ?? []),
        ].map((id) => Number(id)),
        ...advancedFilterPortfolioIds,
      ]),
    ];

    // Sync to global store for each source this widget handles
    stableStoryType.forEach((source) => {
      setPortfoliosToHighlight(source, highlightPortfolioIds);
      setPortfoliosToFilter(source, filterPortfolioIds);
    });

    // Cleanup: clear selections when widget unmounts
    return () => {
      stableStoryType.forEach((source) => {
        setPortfoliosToHighlight(source, []);
        setPortfoliosToFilter(source, []);
      });
    };
  }, [
    filterSelections,
    highlightSelections,
    selectedAdvancedFilterIds,
    queriesQuery.data,
    stableStoryType,
    setPortfoliosToFilter,
    setPortfoliosToHighlight,
  ]);

  return {
    filteredData,
    showSearchResults,
    isSearching,
    searchError,
    searchFirehose: searchFirehose as (params: SearchStoriesParams) => void,
    clearSearchResults,
    selectedAdvancedFilterIds,
    loadNextPage,
    hasMore,
    // Filter panel
    filterSelections,
    setFilterSelections,
    isPanelOpen,
    closePanel,
    filterMenus,
    footerElement,
    clearFilterSelections,
    // Highlight panel
    highlightSelections,
    setHighlightSelections,
    isHighlightPanelOpen,
    closeHighlightPanel,
    highlightMenus,
    highlightFooterElement,
    clearHighlightSelections,
  };
};
